import React from 'react';
import ModuleTable from 'Components/Core/ModuleTable';
import FormElement from 'Components/Core/Form/FormElement';
import MarketdataGroupSelector from './MarketdataGroupSelector';
import MarketdataWatchlistCopyMenu from './MarketdataWatchlistCopyMenu';
import { setMarketdataForcedGroup, useMarketdataForcedGroup } from './marketdataForcedGroup';
import './Marketdata.css';

export default function MarketdataWatchlist(props) {

	const x = props.x;
	const wafId = props.id;
	const dialogFormId = "form-" + props.id;
	const [reloadKey, setReloadKey] = React.useState(0);
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);
	// Checkbox "Trennlinie hinzufuegen" im Add/Edit-Dialog - deaktiviert die
	// uebrigen Felder (Symbol, Kurzbez., Boerse, Bezeichnung) und macht Symbol
	// nicht mehr mandatory, siehe MarketdataWatchlistSave.inc (eigener
	// "isseparator"-Pfad ohne FMP-Validierung).
	const [isSeparator, setIsSeparator] = React.useState(false);
	// dataprovider (Chat vom 2026-08-07, Phase 3+4 der IBKR-Integration) -
	// steuert, ob die IB-Kontraktfelder (ibsectype/IbContractField) sichtbar
	// sind, siehe FormElementDataProvider unten. Default 'ib' (EODHD komplett
	// entfernt, Phase 4).
	const [dataProvider, setDataProvider] = React.useState('ib');
	// Erzwungener Watchlist-Wechsel von aussen (siehe marketdataForcedGroup.js) -
	// "Watchlist bearbeiten" (Stift) in MarketdataQuotes.js/MarketdataCharts.js
	// setzt das, BEVOR dieses Modul aktiviert wird, damit die Verwaltung
	// dieselbe Watchlist zeigt, die in Live/Charts gerade ausgewaehlt war.
	const [forcedGroup, setForcedGroup] = React.useState(null);
	const consumeForcedGroup = useMarketdataForcedGroup(x, setForcedGroup);
	// Kopieren in eine andere Watchlist (Chat vom 2026-08-10). null = kein
	// Dialog offen; sonst { watchlistid } (einzelner Eintrag) oder { all: true }
	// (alle Eintraege der aktuellen Watchlist). Der Ziel-Picker (siehe
	// MarketdataWatchlistCopyMenu) ruft danach copyTo(targetGroupId) auf.
	const [copySource, setCopySource] = React.useState(null);
	// Globale Suchbox in der Filterzeile (Live-Stil, Chat vom 2026-08-12) -
	// filtert die Liste ueber Symbol/Kurzbez./Bezeichnung/ISIN (siehe
	// searchColumns unten, ausgewertet in Table.js::applyFilters).
	const [searchText, setSearchText] = React.useState('');
	// Erhoehen erzwingt ein Remount des Watchlist-Auswaehlers (laedt die
	// Gruppenliste neu + waehlt Default), z.B. nach dem Loeschen der aktuellen
	// Watchlist (Chat vom 2026-08-12).
	const [selectorKey, setSelectorKey] = React.useState(0);

	// Aktuelle Watchlist loeschen (eigenes Icon in der Filterzeile, da das
	// Loeschen aus dem Auswahl-Dropdown entfernt wurde - Chat vom 2026-08-12).
	// Sicherheitsabfrage via x.wau.yesNo; das Backend
	// (MarketdataWatchlistGroupDelete) verweigert die letzte verbleibende Liste.
	function deleteCurrentWatchlist() {
		if (!selectedGroupId) {
			return;
		}
		x.wau.yesNo('Aktuelle Watchlist samt aller enthaltenen Symbole wirklich löschen?', (result) => {
			if (result !== 1) {
				return;
			}
			x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistGroupDelete', { id: selectedGroupId }, (res) => {
				if (res.data.returncode !== 1) {
					x.wah.addHint(res.data.content?.message || 'Watchlist konnte nicht gelöscht werden.', false);
					return;
				}
				// Auswaehler neu aufsetzen -> laedt Gruppen neu und meldet die neue
				// Default-Watchlist per onSelect (changeGroup), was auch die Tabelle
				// neu laedt.
				setSelectedGroupId(null);
				setSelectorKey((current) => current + 1);
			});
		}, true);
	}

	function copyTo(targetGroupId) {
		const body = { targetgroupid: targetGroupId };
		if (copySource && copySource.all) {
			body.sourcegroupid = selectedGroupId;
		} else if (copySource && copySource.watchlistid) {
			body.watchlistid = copySource.watchlistid;
		} else {
			setCopySource(null);
			return;
		}
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistCopy', body, (result) => {
			setCopySource(null);
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				x.wah.addHint(content.message || 'Kopieren fehlgeschlagen.', false);
				return;
			}
			// Kein Auto-Close (Nutzerwunsch, Chat vom 2026-08-10): die Meldung
			// enthaelt wichtige Infos (wie viele kopiert/uebersprungen wurden),
			// die der Nutzer in Ruhe lesen und selbst schliessen koennen soll.
			x.wah.addHint(content.message || 'Kopiert.', false);
		});
	}

	// Der Speichern-Button bleibt sonst nach dem Anklicken der Checkbox
	// deaktiviert: x.waf.checkChanged() (WebappForm.js) prueft zuerst
	// mandatoryOk() - im selben Klick-Event ist "symbol" dort aber noch als
	// mandatory=true UND leer registriert, weil FormElement das mandatory-Flag
	// erst in einem EIGENEN useEffect aktualisiert (Prop mandatory={!isSeparator}
	// aendert sich ja erst durch DIESEN Re-Render). Nach dem Commit (wenn
	// Kind-Effekte inkl. FormElement bereits gelaufen sind) den Button-Status
	// hier nochmal neu berechnen.
	React.useEffect(() => {
		if (x.waf[wafId]) {
			x.waf[wafId].onFormChanged();
		}
	}, [isSeparator, x, wafId]);

	// Table.js ruft onAddColumn(key, value) je Zeile spaltenweise auf, ohne
	// die gesamte Zeile mitzugeben - "isseparator" steht in der SELECT-Spalten-
	// reihenfolge (siehe MarketdataWatchlistList.inc) bewusst VOR den uebrigen
	// Feldern, damit dieser Merker beim Verarbeiten von symbol/shortlabel/... der
	// GLEICHEN Zeile schon gesetzt ist.
	let rowIsSeparator = false;

	// Mehrere Watchlists (Gruppen) statt einer einzigen globalen Liste - bewusst
	// ohne eigenen Menüpunkt (User-Vorgabe), stattdessen eine Auswahlbox direkt
	// hier und im Pendant MarketdataQuotes.js (siehe MarketdataGroupSelector.js).
	function changeGroup(groupId) {
		setSelectedGroupId(groupId);
		setReloadKey((current) => current + 1);
	}

	// Nach Drag-and-Drop-Umsortierung (siehe draggableRows/onReorder in
	// Table.js) die neue Reihenfolge als sortorder=Index speichern und die
	// Tabelle danach neu laden, damit die angezeigte "Sortierung"-Spalte
	// wieder zu den tatsaechlichen Werten passt (Table.js selbst zeigt die
	// neue Reihenfolge zwar sofort an, aber mit den alten sortorder-Zahlen).
	function handleReorder(newData) {
		const order = newData.map((row) => row.watchlistid);
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistReorder', { order }, (result) => {
			if (result.data.returncode === 1) {
				setReloadKey((current) => current + 1);
			}
		});
	}

	const formFields =
		<React.Fragment>
			<HiddenGroupField x={x} wafId={wafId} groupId={selectedGroupId} />
			<FormElement
				type="checkbox"
				x={x}
				wafId={wafId}
				id="isseparator"
				title="Trennlinie hinzufügen"
				onChange={(e) => setIsSeparator(e.target.checked)}
			/>
			{/* Optionaler Titel der Trennlinie (Chat vom 2026-08-10) - eigene
			    Feld-id "separatorlabel" (NICHT "label", das ist fuer die
			    Symbol-Bezeichnung belegt; zwei Felder mit gleicher id wuerden
			    sich die x.waf-Ref ueberschreiben). Nur bei aktivierter
			    Trennlinie sichtbar, sonst per display:none versteckt (Ref bleibt
			    gueltig, siehe Kommentar unten). Der Titel wird in der Live-
			    Ansicht zentriert auf der Linie angezeigt. */}
			<div style={isSeparator ? undefined : { display: 'none' }}>
				<FormElement type="text" x={x} wafId={wafId} id="separatorlabel" mandatory={false} title="Titel der Trennlinie (optional)" fieldValue="" maxLength="100" />
			</div>
			{/* Bei aktivierter Checkbox bewusst per CSS (display:none) versteckt,
			    NICHT bedingt aus dem DOM entfernt - x.waf haelt pro Feld eine feste
			    React-Ref (siehe WebappForm.js/initField), die bei einem Unmount auf
			    null faellt; mandatoryOk()/checkChanged() lesen reference.current
			    aber ungeprueft und wuerden dann abstuerzen. display:none haelt die
			    Refs gueltig, versteckt aber Label+Eingabe vollstaendig (anders als
			    disabled, das nur ausgegraut wird und die Titel weiter anzeigt). */}
			<div style={isSeparator ? { display: 'none' } : undefined}>
				<FormElementDataProvider x={x} wafId={wafId} onChange={setDataProvider} />
				{/* Chat vom 2026-08-07 (Nutzerfeedback: "Suchfeld sollte ganz oben
				    stehen") - die IB-Kontraktsuche ist der erste, wichtigste Schritt
				    beim Anlegen eines neuen Symbols, steht deshalb jetzt direkt nach
				    der Datenquelle-Auswahl, statt unten nach Kurzbez. versteckt.
				    Bewusst IMMER gemountet, per CSS (display:none) versteckt statt
				    bedingt aus dem DOM entfernt (siehe Kommentar bei IbContractField
				    weiter unten zur Ref-Problematik). */}
				<div style={dataProvider === 'ib' ? undefined : { display: 'none' }}>
					<IbContractField x={x} wafId={wafId} />
				</div>
				{/* Chat vom 2026-08-07 (Nutzerfeedback: "einige Felder redundant,
				    Dialog komplett ueberarbeiten") - Symbol/Börse zeigten bei IB
				    dieselbe Information NOCHMAL wie IB-Symbol/Primary-Exchange oben
				    (und wurden tatsaechlich gar nicht mehr befuellt, da die alte
				    EODHD-Symbolsuche entfernt wurde - fuer neue IB-Eintraege blieben
				    sie schlicht leer, ein echter Bug). Bei IB jetzt komplett
				    versteckt und automatisch aus der IB-Kontraktsuche befuellt
				    (siehe IbContractField::selectResult()). Bei Kraken (nur 3 feste
				    Krypto-Symbole, keine Suche) bleiben sie sichtbar und manuell
				    editierbar. Die volle "Bezeichnung" bleibt dagegen IMMER sichtbar
				    (Nutzerfeedback: "die komplette [Bezeichnung] gar nicht mehr [zu
				    sehen]") - bei IB gesperrt/automatisch befuellt, bei Kraken
				    weiterhin frei editierbar. */}
				<div style={dataProvider === 'ib' ? { display: 'none' } : undefined}>
					<div className="marketdata-basic-fields-grid">
						<FormElement type="text" x={x} wafId={wafId} id="symbol" mandatory={!isSeparator} title="Symbol" fieldValue="" maxLength="20" />
						<FormElement type="text" x={x} wafId={wafId} id="exchange" mandatory={false} title="Börse" fieldValue="US" maxLength="20" />
					</div>
				</div>
				<FormElement type="text" x={x} wafId={wafId} id="label" mandatory={false} readOnly={dataProvider === 'ib'} disabled={dataProvider === 'ib'} title="Bezeichnung" fieldValue="" maxLength="100" />
				{/* Kurzbezeichnung ist bei JEDER Datenquelle sichtbar und editierbar
				    (Nutzer will oft einen kuerzeren eigenen Namen als den von der
				    Suche gelieferten vollen Namen). */}
				<FormElement type="text" x={x} wafId={wafId} id="shortlabel" mandatory={false} title="Kurzbezeichnung" fieldValue="" maxLength="20" />
			</div>
		</React.Fragment>

	// Kopf-Toolbar: NUR noch die Aktions-Icons (Live/Charts/Kopieren). Die
	// Watchlist-Auswahl ist in die Filterzeile ueber der Tabelle gewandert
	// (Chat vom 2026-08-12), analog zur Live-Ansicht.
	const watchlistSelector =
		<React.Fragment>
			{/* Umgekehrter Weg zu "Watchlist bearbeiten" (Stift) in Live/Charts -
			    springt zur jeweils gerade ausgewaehlten Watchlist statt der
			    Default-Watchlist, siehe marketdataForcedGroup.js. Der Toolbar-
			    Container (.module-table-title-toolbar) nutzt flex-direction:
			    row-reverse mit nur 4px gap - ohne zusaetzlichen Abstand kleben
			    diese Icons optisch direkt an der Watchlist-Auswahlbox. */}
			<i
				className="bi bi-broadcast module-icon"
				style={{ marginRight: '10px' }}
				title="Watchlist Live anzeigen"
				onClick={() => {
					setMarketdataForcedGroup(x, selectedGroupId);
					x.wam.activate('marketdata-quotes');
				}}
			/>
			<i
				className="bi bi-graph-up module-icon"
				title="Charts anzeigen"
				onClick={() => {
					setMarketdataForcedGroup(x, selectedGroupId);
					x.wam.activate('marketdata-charts');
				}}
			/>
			{/* Alle Eintraege der aktuellen Watchlist in eine andere kopieren
			    (Chat vom 2026-08-10). */}
			<i
				className="bi bi-copy module-icon"
				title="Alle Einträge in andere Watchlist kopieren"
				onClick={() => selectedGroupId && setCopySource({ all: true })}
			/>
		</React.Fragment>

	// Filterzeile ueber der Tabelle (Live-Stil, Chat vom 2026-08-12): Suchbox
	// links, Watchlist-Auswahl rechts. Die Suche filtert die Liste ueber
	// searchColumns (siehe ModuleTable/Table.js).
	const filterToolbar =
		<div className="marketdata-quotes-filter marketdata-watchlist-filter">
			<i className="bi bi-search"></i>
			<input
				type="text"
				value={searchText}
				onChange={(e) => setSearchText(e.target.value)}
				onKeyDown={(e) => { if (e.key === 'Escape') { setSearchText(''); e.stopPropagation(); } }}
				placeholder="Symbol/Kurzbezeichnung filtern…"
			/>
			{searchText !== '' &&
				<i className="bi bi-x-lg marketdata-quotes-filter-clear" title="Filter löschen" onClick={() => setSearchText('')}></i>
			}
			<span className="marketdata-watchlist-filter-actions">
				<MarketdataGroupSelector key={selectorKey} x={x} id={props.id} onSelect={changeGroup} forcedGroup={forcedGroup} inline={true} />
				<i
					className="bi bi-trash marketdata-watchlist-delete-group"
					title="Aktuelle Watchlist löschen"
					onClick={deleteCurrentWatchlist}
				/>
			</span>
		</div>;

	return (
		<React.Fragment>
		<ModuleTable
			id={props.id}
			tableId="watchlistid"
			dialogFormId={dialogFormId}
			x={x}
			classes="module-marketdata-watchlist"
			tableClasses="marketdata-watchlist-table"
			title={props.title}
			extraToolbar={watchlistSelector}
			toolbar={filterToolbar}
			searchText={searchText}
			searchColumns={['symbol', 'shortlabel', 'label', 'isin']}
			onShow={consumeForcedGroup}
			addDeleteRow={true}
			addTitle="Symbol zur Watchlist hinzufügen"
			formTitle="Watchlist-Symbol"
			focusField={dataProvider === 'ib' ? 'ibsearch' : 'symbol'}
			module="Marketdata"
			listTask="MarketdataWatchlistList"
			listParams={'&groupid=' + selectedGroupId}
			deleteTask="MarketdataWatchlistDelete"
			reloadKey={reloadKey}
			draggableRows={true}
			onReorder={handleReorder}
			onAfterSend={() => {
				// Nach dem Speichern (v.a. einer Trennlinie) die Checkbox wieder
				// zuruecksetzen, sonst bleibt sie beim naechsten "+" angehakt und
				// die uebrigen Felder blieben faelschlich versteckt.
				x.waf.set(wafId, 'isseparator', 0);
				setIsSeparator(false);
				x.waf.set(wafId, 'dataprovider', 'ib');
				setDataProvider('ib');
				x.waf.set(wafId, 'separatorlabel', '');
			}}
			onAddHeader={(addHeader) => {
				addHeader('Symbol');
				addHeader('Kurzbez.');
				addHeader('Börse');
				addHeader('Bezeichnung');
				addHeader('Sortierung');
				addHeader('ISIN');
				addHeader('Quelle');
			}}
			onRowClasses={(item) => (Number(item.isseparator) ? 'marketdata-watchlist-separator-row' : '')}
			onAddColumn={(key, value) => {
				switch (key) {
					case 'watchlistid':
						return false;
					case 'isseparator':
						// "isseparator" steht bewusst als erste echte Spalte in der
						// SELECT-Reihenfolge (siehe MarketdataWatchlistList.inc), damit
						// dieser Merker fuer die uebrigen Spalten DERSELBEN Zeile schon
						// gesetzt ist, bevor sie verarbeitet werden.
						rowIsSeparator = !!Number(value);
						return false;
					case 'symbol':
						// Trennlinie: leer (der Titel wird ueber die "label"-
						// Spalte zentriert auf der Linie gerendert, siehe unten).
						return rowIsSeparator ? '' : value;
					case 'shortlabel':
						return rowIsSeparator ? '' : value;
					case 'exchange':
						return rowIsSeparator ? '' : value;
					case 'label':
						// Bei einer Trennlinie ist "label" der (optionale) Titel -
						// als absolut positioniertes, zentriertes Element ueber der
						// Linie (siehe .marketdata-watchlist-separator-title). Bei
						// echten Symbolen bleibt es die normale Bezeichnung.
						return rowIsSeparator
							? (value ? <span className="marketdata-watchlist-separator-title">{value}</span> : '')
							: value;
					case 'sortorder':
						// Bei Trennlinien ausblenden (frueher global per opacity:0,
						// das ist jetzt wegen des sichtbaren Titels entfernt, siehe
						// Marketdata.css).
						return rowIsSeparator ? '' : value;
					case 'isin':
						return rowIsSeparator ? '' : (value || '–');
					// comdirectinstrumentid steuert hier nichts mehr - der "Kaufen"-
					// Button ist in die Live-Ansicht umgezogen (Chat vom 2026-07-26,
					// siehe MarketdataQuotes.js), die Watchlist-Verwaltung ist reine
					// Stammdatenpflege.
					case 'comdirectinstrumentid':
						return false;
					case 'dataprovider':
						return rowIsSeparator ? '' : DATA_PROVIDER_LABELS[value] || value;
					// Die uebrigen IB-Felder werden hier nicht als eigene Spalte
					// angezeigt (die "Quelle"-Spalte oben reicht fuer die Uebersicht) -
					// aber im Bearbeiten-Dialog gebraucht, siehe onEditRow().
					case 'ibsectype': case 'ibsymbol': case 'ibexchange': case 'ibcurrency': case 'ibprimaryexchange':
						return false;
					default:
						return false;
				}
			}}
			onEditRow={(e, rowFields, rowId) => {
				const separator = !!Number(rowFields.isseparator);
				x.waf.setAddSend(wafId, 'id', rowFields.watchlistid);
				x.waf.set(wafId, 'symbol', rowFields.symbol);
				x.waf.set(wafId, 'shortlabel', rowFields.shortlabel);
				x.waf.set(wafId, 'exchange', rowFields.exchange);
				x.waf.set(wafId, 'label', rowFields.label);
				const provider = rowFields.dataprovider || 'ib';
				x.waf.set(wafId, 'dataprovider', provider);
				setDataProvider(provider);
				x.waf.set(wafId, 'ibsectype', rowFields.ibsectype || 'STK');
				x.waf.set(wafId, 'ibsymbol', rowFields.ibsymbol || '');
				x.waf.set(wafId, 'ibexchange', rowFields.ibexchange || '');
				x.waf.set(wafId, 'ibcurrency', rowFields.ibcurrency || '');
				x.waf.set(wafId, 'ibprimaryexchange', rowFields.ibprimaryexchange || '');
				x.waf.set(wafId, 'isseparator', separator ? 1 : 0);
				setIsSeparator(separator);
				// Titel der Trennlinie (Chat vom 2026-08-10) - bei Trennlinien
				// ist rowFields.label der Titel, ins eigene Feld uebernehmen.
				x.waf.set(wafId, 'separatorlabel', separator ? (rowFields.label || '') : '');
				x.waf.hide(wafId, 'message');
				x.wad[dialogFormId].setTitle(separator ? 'Trennlinie bearbeiten' : 'Watchlist-Symbol bearbeiten');
				// Chat vom 2026-08-07 (Bugreport: Fokus beim Bearbeiten landet nicht
				// im Suchfeld) - dieser Aufruf hier war bisher fest auf "symbol"
				// verdrahtet, unabhaengig vom "focusField"-Prop weiter unten (das
				// gilt nur fuer den "+"-Button/addRow() in ModuleTable.js). "symbol"
				// ist bei IB inzwischen versteckt (display:none) - der Fokus lief
				// dadurch ins Leere. Jetzt dieselbe Logik wie im focusField-Prop.
				x.wad[dialogFormId].show(() => { x.waf.focus(wafId, provider === 'ib' ? 'ibsearch' : 'symbol') });
			}}
			formFields={formFields}
			formSendTask="MarketdataWatchlistSave"
			extraRowActionHeader={<i className="bi bi-copy" title="In andere Watchlist kopieren" />}
			extraRowAction={(item) => (
				// Einzelnen Eintrag in eine andere Watchlist kopieren (Chat vom
				// 2026-08-10). stopPropagation, damit nicht zusaetzlich der
				// Zeilen-Klick (Bearbeiten) feuert.
				<i
					className="bi bi-copy marketdata-watchlist-copy-icon"
					title="In andere Watchlist kopieren"
					onClick={(e) => { e.stopPropagation(); setCopySource({ watchlistid: item.watchlistid }); }}
				/>
			)}
		/>
		{copySource &&
			<MarketdataWatchlistCopyMenu
				x={x}
				title={copySource.all ? 'Alle Einträge kopieren nach…' : 'Eintrag kopieren nach…'}
				excludeGroupId={selectedGroupId}
				onSelect={copyTo}
				onClose={() => setCopySource(null)}
			/>
		}
		</React.Fragment>
	)

};

const DATA_PROVIDER_LABELS = {
	ib: 'IB',
	kraken: 'Kraken',
};

// Datenquelle-Auswahl (Chat vom 2026-08-07, Phase 3 der IBKR-Integration) -
// ersetzt die bisherige automatische Ableitung aus der Anwesenheit eines
// IG-Epics (IG ist komplett entfernt). Eigenes, schlankes <select> statt
// FormElement type="select" (Vorbild: FormElementFontSize in
// Components/Member/Settings.js) - dort fuer 3 feste Optionen begruendet,
// gilt hier genauso. Default 'ib' (Phase 4: EODHD-Option komplett entfernt).
function FormElementDataProvider({ x, wafId, onChange }) {
	const reference = React.useRef(null);
	React.useEffect(() => {
		x.waf.initField(wafId, 'dataprovider', reference, 'ib', 'dataprovider');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<div className="webapp-form-item mb-2">
			<label className="form-label">Datenquelle</label>
			<select
				ref={reference}
				className="form-control"
				onChange={(e) => {
					x.waf.set(wafId, 'dataprovider', e.target.value);
					onChange(e.target.value);
				}}
			>
				<option value="ib">Interactive Brokers</option>
				<option value="kraken">Kraken (nur BTC/ETH/LINK)</option>
			</select>
		</div>
	);
}

// IB-Kontraktsuche (Chat vom 2026-08-07, Phase 3 der IBKR-Integration) -
// ersetzt die bisherige IgEpicField. Anders als IG braucht IB fuer Aktien/
// Futures/Indizes mehrere Felder (Kontrakttyp, IB-eigenes Symbol, Exchange,
// Waehrung) - die werden AUSSCHLIESSLICH ueber die Suche befuellt (User-
// Vorgabe: manuelles Editieren dieser IB-eigenen Werte ist unsinnig, da sie
// exakt der von IB gelieferten Kontraktdefinition entsprechen muessen, kein
// freies Textfeld). Kompaktes 2-Spalten-Layout statt vier untereinander
// gestapelter voller Zeilen (Nutzerfeedback: Dialog lief unten aus dem
// Bildschirm).
function IbContractField({ x, wafId }) {
	const [query, setQuery] = React.useState('');
	const [results, setResults] = React.useState([]);
	const debounceRef = React.useRef(null);
	const searchSequence = React.useRef(0);
	const secTypeRef = React.useRef(null);
	const inputRef = React.useRef(null);

	React.useEffect(() => {
		x.waf.initField(wafId, 'ibsectype', secTypeRef, 'STK', 'ibsectype');
		// Als eigenes Feld bei x.waf registrieren (Nutzerwunsch: Fokus soll
		// beim Oeffnen des Dialogs direkt hier liegen, nicht auf dem
		// versteckten Symbol-Feld) - damit ModuleTable.js' focusField-
		// Mechanismus (x.waf.focus(wafId, focusField)) dieses Feld erreichen
		// kann, siehe MarketdataWatchlist.js/focusField-Prop.
		x.waf.initField(wafId, 'ibsearch', inputRef, '', 'ibsearch');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleChange(e) {
		const value = e.target.value;
		setQuery(value);
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		if (value.trim().length < 2) {
			searchSequence.current += 1;
			setResults([]);
			return;
		}
		searchSequence.current += 1;
		const currentSequence = searchSequence.current;
		debounceRef.current = setTimeout(() => {
			x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataIbSearch', { query: value }, (result) => {
				if (currentSequence !== searchSequence.current) {
					return;
				}
				if (result.data.returncode === 1) {
					setResults(result.data.content?.results || []);
				}
			});
		}, 300);
	}

	function selectResult(row) {
		x.waf.set(wafId, 'ibsectype', row.secType);
		if (secTypeRef.current) {
			secTypeRef.current.value = row.secType;
		}
		x.waf.set(wafId, 'ibsymbol', row.symbol);
		x.waf.set(wafId, 'ibexchange', row.exchange);
		x.waf.set(wafId, 'ibcurrency', row.currency);
		// Chat vom 2026-08-07 (Nutzerfrage: welche Werte sind bei
		// Primary-Exchange moeglich/koennte man ein Dropdown anbieten) - IBs
		// Handelsplatz-Codes sind zu zahlreich/unvorhersagbar fuer eine feste
		// Liste, ABER die Suche liefert den korrekten Wert selbst schon mit
		// (siehe resolveContractSearch() in IbClient.mjs) - deshalb jetzt
		// automatisch befuellt statt manuell einzutippen.
		x.waf.set(wafId, 'ibprimaryexchange', row.primaryExchange || '');
		// Chat vom 2026-08-07 (Nutzerfeedback: Dialog ueberarbeiten, redundante
		// Felder) - Symbol/Börse/Bezeichnung/Kurzbez. sind bei IB nicht mehr
		// sichtbar, muessen also HIER automatisch aus dem IB-Treffer befuellt
		// werden (vorher blieben sie schlicht leer, seit die alte EODHD-
		// Symbolsuche entfernt wurde - echter Bug, nicht nur ein
		// Anzeigeproblem). "symbol" ist unsere interne DB-Kennung -
		// Primary-Exchange als Suffix haengt an, wo vorhanden (analog dem
		// bisherigen EODHD-Format "AAPL.US"), sonst nur das reine IB-Symbol
		// (Futures/Indizes haben keinen Primary-Exchange).
		const symbol = row.primaryExchange ? row.symbol + '.' + row.primaryExchange : row.symbol;
		x.waf.set(wafId, 'symbol', symbol);
		x.waf.set(wafId, 'exchange', row.primaryExchange || row.exchange);
		const label = row.description || row.symbol;
		x.waf.set(wafId, 'label', label);
		x.waf.set(wafId, 'shortlabel', label.split(' ')[0].slice(0, 20));
		setQuery('');
		setResults([]);
	}

	return (
		<div className="marketdata-ib-contract-field">
			<div className="marketdata-symbol-search">
				<label className="marketdata-symbol-search-label">IB-Kontrakt suchen (füllt die Felder unten aus)</label>
				<input
					ref={inputRef}
					type="text"
					className="form-control rounded-pill"
					value={query}
					onChange={handleChange}
					placeholder="z.B. Apple, Gold, Nasdaq 100"
					autoComplete="off"
				/>
				{results.length > 0 &&
					<ul className="marketdata-symbol-search-results">
						{results.map((row, idx) => (
							<li key={row.symbol + '-' + row.exchange + '-' + idx} onClick={() => selectResult(row)}>
								<strong>{row.symbol}</strong> <span>{row.secType} · {row.exchange} · {row.currency}{row.description ? ' · ' + row.description : ''}</span>
							</li>
						))}
					</ul>
				}
			</div>
			{/* Ausschliesslich ueber die Suche oben befuellt (siehe
			    selectResult()) - "disabled" statt eines editierbaren Feldes,
			    Werte bleiben trotzdem ueber die Ref lesbar (x.waf.set() schreibt
			    weiterhin direkt auf reference.current.value, unabhaengig vom
			    disabled-Status). Kompaktes 2-Spalten-Grid statt vier vollen
			    Zeilen. */}
			<div className="marketdata-ib-contract-grid">
				<div className="webapp-form-item mb-2">
					<label className="form-label">Kontrakttyp</label>
					<select ref={secTypeRef} className="form-control" disabled>
						<option value="STK">Aktie</option>
						<option value="FUT">Future</option>
						<option value="IND">Index</option>
					</select>
				</div>
				<FormElement type="text" x={x} wafId={wafId} id="ibsymbol" mandatory={false} readOnly={true} disabled={true} title="IB-Symbol" fieldValue="" maxLength="20" />
				<FormElement type="text" x={x} wafId={wafId} id="ibexchange" mandatory={false} readOnly={true} disabled={true} title="IB-Exchange" fieldValue="" maxLength="20" />
				<FormElement type="text" x={x} wafId={wafId} id="ibcurrency" mandatory={false} readOnly={true} disabled={true} title="Währung" fieldValue="USD" maxLength="10" />
				{/* Chat vom 2026-08-07 (Nutzerfrage: welche Werte moeglich/Dropdown
				    anbieten?) - IBs Handelsplatz-Codes sind zu zahlreich fuer eine
				    feste Liste, ABER die Suche liefert den korrekten Wert selbst
				    schon mit (siehe resolveContractSearch()) - deshalb jetzt
				    ebenfalls automatisch befuellt statt manuell einzutippen. */}
				<FormElement type="text" x={x} wafId={wafId} id="ibprimaryexchange" mandatory={false} readOnly={true} disabled={true} title="Primary-Exchange" fieldValue="" maxLength="20" />
			</div>
		</div>
	);
}

// Unsichtbares Feld, das die aktuell ausgewaehlte Watchlist (groupid) beim
// Anlegen/Bearbeiten eines Symbols mitschickt - der Nutzer waehlt die
// Watchlist ueber die Auswahlbox im Toolbar, nicht im Dialog selbst.
function HiddenGroupField({ x, wafId, groupId }) {
	const inputRef = React.useRef(null);

	React.useEffect(() => {
		x.waf.initField(wafId, 'groupid', inputRef, '', 'groupid');
	}, [x, wafId]);

	React.useEffect(() => {
		if (groupId !== null && x.waf.isField(wafId, 'groupid')) {
			x.waf.set(wafId, 'groupid', groupId, true);
		}
	}, [x, wafId, groupId]);

	return <input type="hidden" ref={inputRef} />;
}
